import React, { useMemo, useState } from 'react';
import { lerVeredito, notasVigentes } from './ocVeredito';

// ═══════════════════════════════════════════════════════════════
// src/OcTrilhaAgencias.jsx — RiskMind x agencia, DUAS SERIES PARALELAS
//
// O QUE ESTA TELA E, E O QUE ELA NAO E
//
// Nao e uma lista de pontos de virada. E o HISTORICO: uma linha por observacao,
// com o que o RiskMind marcava e o que a agencia mantinha NAQUELE momento. A
// nota da agencia entre duas acoes nao desaparece — vale ate a acao seguinte, e
// por isso e repetida (carregada para frente) em cada linha, com o marcador de
// acao so onde a agencia de fato se moveu.
//
// AS LETRAS NAO SE COMPARAM. AS PDs, SIM — COM RESSALVA.
//
// `rm` e a escala interna do RiskMind; `(bra)`, `br…` e `.br` sao escalas
// NACIONAIS de Fitch, S&P e Moody's; e ha ainda a escala GLOBAL. Nenhuma delas
// se converte na outra por tabela, e as proprias agencias dizem isso da escala
// nacional: ela e uma ordenacao RELATIVA dentro do pais, nao uma probabilidade.
//
// Por isso a coluna "PD implicita" so e preenchida quando a nota esta em escala
// GLOBAL — a unica para a qual existe taxa de default publicada. Para escala
// nacional a celula fica vazia e diz por que. Nao ha conversao honesta.
//
// E mesmo onde ela e preenchida, os dois numeros nao sao o mesmo objeto: o da
// agencia e MEDIA DE COORTE DE LONGO PRAZO (S&P, 1981-2024, global, todos os
// setores); o nosso e saida point-in-time de um modelo, para uma companhia, num
// trimestre. Mesma unidade, horizontes diferentes. O rodape carimba isso, e a
// coluna Δ existe para tornar a diferenca visivel em vez de sugerir equivalencia.
// ═══════════════════════════════════════════════════════════════

const C = {
  navy: '#0A1628', blue: '#1A3F6F', amber: '#C87B2A', gold: '#E8C56D',
  red: '#DC2626', green: '#15803D', amber2: '#D97706', agency: '#8067B7',
  muted: '#6b7280', line: '#e5e7eb', ink: '#0f172a', paper: '#ffffff',
};

const FONTE_RM = 'riskmind';

/* ---------------------------------------------------------------------------
   PD IMPLICITA DA NOTA DA AGENCIA

   Fonte unica, citada na tela: S&P Global Ratings, "2024 Annual Global
   Corporate Default And Rating Transition Study", Tabela 4 — taxa de default
   de UM ANO, media ponderada 1981-2024, corporates globais.

   Categoria, nao modificador: a propria tabela publica por categoria, e usar
   modificador exigiria interpolar o que a fonte nao afirma. BB- entra como BB.

   NAO existe tabela equivalente para escala nacional. Inventar uma seria o
   mesmo erro de denominador que este projeto ja cometeu tres vezes: um numero
   que parece uma taxa e e taxa de outra coisa.
   --------------------------------------------------------------------------- */
const PD_GLOBAL_1A = {
  AAA: 0.00, AA: 0.02, A: 0.05, BBB: 0.14, BB: 0.56, B: 2.93, CCC: 26.12, CC: 26.12, C: 26.12, D: null,
};
const FONTE_PD = "S&P Global, 2024 Annual Global Corporate Default And Rating Transition Study, Tab. 4 - media ponderada 1981-2024, 1 ano, corporates globais";

/* Moody's escreve Aaa/Ba2/Caa1; a tabela acima esta em notacao S&P/Fitch. */
const MOODY_PARA_SP = {
  Aaa: 'AAA', Aa1: 'AA', Aa2: 'AA', Aa3: 'AA', A1: 'A', A2: 'A', A3: 'A',
  Baa1: 'BBB', Baa2: 'BBB', Baa3: 'BBB', Ba1: 'BB', Ba2: 'BB', Ba3: 'BB',
  B1: 'B', B2: 'B', B3: 'B', Caa1: 'CCC', Caa2: 'CCC', Caa3: 'CCC', Ca: 'CC', C: 'C',
};

/** Categoria da nota, sem modificador. null quando nao ha como afirmar. */
function categoria(nota) {
  if (!nota) return null;
  const n = String(nota).trim();
  if (MOODY_PARA_SP[n]) return MOODY_PARA_SP[n];
  const m = n.match(/^(AAA|AA|A|BBB|BB|B|CCC|CC|C|D)[+-]?$/);
  return m ? m[1] : null;
}

/**
 * PD implicita da nota. SO para escala global — ver o cabecalho.
 * Devolve { pd, motivo } e o motivo viaja para a tela quando pd e null.
 */
export function pdImplicita(nota, escala) {
  if (!nota) return { pd: null, motivo: null };
  if (escala !== 'global') {
    return { pd: null, motivo: 'escala nacional nao tem taxa de default publicada' };
  }
  const cat = categoria(nota);
  if (!cat) return { pd: null, motivo: 'nota fora da tabela' };
  const pd = PD_GLOBAL_1A[cat];
  if (pd === null || pd === undefined) {
    return { pd: null, motivo: cat === 'D' ? 'ja em default' : 'sem taxa para esta categoria' };
  }
  return { pd, motivo: null, categoria: cat };
}

const d10 = (d) => (d ? String(d).slice(0, 10) : '');
const dBR = (d) => { const s = d10(d); return s ? s.slice(8, 10) + '/' + s.slice(5, 7) + '/' + s.slice(0, 4) : 'n/d'; };
const qlabel = (d) => {
  const s = d10(d); if (!s) return '';
  const m = +s.slice(5, 7); const q = m <= 3 ? 1 : m <= 6 ? 2 : m <= 9 ? 3 : 4;
  return q + 'T' + s.slice(2, 4);
};
const pd1 = (v) => (v == null ? '—' : (+v).toFixed(2).replace('.', ',') + '%');

const ORDEM_RM = ['AAA', 'AA', 'A', 'BBB', 'BB', 'B', 'CCC', 'CC', 'C', 'D'];
const corRating = (r) => {
  const g = ORDEM_RM.indexOf(String(r || '').toUpperCase());
  if (g < 0) return C.muted;
  return g >= 7 ? C.red : g >= 5 ? C.amber2 : g >= 3 ? C.blue : C.green;
};

/** A observacao da agencia vigente numa data: a ultima acao com data <= alvo. */
function vigenteEm(ordenados, dataAlvo) {
  let achado = null;
  for (const p of ordenados) {
    if (d10(p.obs_date) <= dataAlvo) achado = p; else break;
  }
  return achado;
}

/** Monta as linhas da tabela. Exportada para poder ser afirmada por teste. */
export function montarLinhas(track) {
  const pts = Array.isArray(track) ? track : [];
  const rm = pts.filter((p) => p.fonte === FONTE_RM)
    .sort((a, b) => d10(a.obs_date).localeCompare(d10(b.obs_date)));
  const ag = pts.filter((p) => p.fonte !== FONTE_RM)
    .sort((a, b) => d10(a.obs_date).localeCompare(d10(b.obs_date)));

  const datas = [...new Set(rm.concat(ag).map((p) => d10(p.obs_date)))].sort();
  const inicioAg = ag.length ? d10(ag[0].obs_date) : null;

  const linhas = datas.map((data) => {
    const rmPt = rm.find((p) => d10(p.obs_date) === data) || null;
    const agVig = vigenteEm(ag, data);
    const agAcao = ag.find((p) => d10(p.obs_date) === data) || null;
    const imp = agVig ? pdImplicita(agVig.rating, agVig.escala) : { pd: null, motivo: null };
    const rmPd = rmPt && rmPt.pd != null ? Number(rmPt.pd) : null;
    return {
      data,
      dataBase: rmPt && rmPt.detalhe ? d10(rmPt.detalhe.data_base) : null,
      obsOrigem: rmPt && rmPt.detalhe ? rmPt.detalhe.obs_origem : null,
      rmRating: rmPt ? rmPt.rating : null,
      rmPd,
      agRating: agVig ? agVig.rating : null,
      agFonte: agVig ? agVig.fonte : null,
      agEscala: agVig ? agVig.escala : null,
      agDesde: agVig ? d10(agVig.obs_date) : null,
      agAcao: !!agAcao,
      agPd: imp.pd,
      agPdMotivo: imp.motivo,
      // p.p. de diferenca. So existe quando os dois lados existem.
      delta: (rmPd != null && imp.pd != null) ? rmPd - imp.pd : null,
      agAusencia: agVig ? null : (inicioAg ? 'antes_da_cobertura' : 'sem_cobertura'),
    };
  }).reverse();

  const fontes = [...new Set(ag.map((p) => p.fonte))];
  return {
    linhas,
    cobertura: {
      temAgencia: ag.length > 0,
      fontes,
      desde: inicioAg,
      ate: ag.length ? d10(ag[ag.length - 1].obs_date) : null,
      nPontosAg: ag.length,
      nPontosRm: rm.length,
      escalas: [...new Set(ag.map((p) => p.escala).filter(Boolean))],
      temGlobal: ag.some((p) => p.escala === 'global'),
    },
  };
}

const box = { background: C.paper, border: '1px solid ' + C.line, borderRadius: 10, padding: '14px 16px' };
const th = {
  textAlign: 'left', padding: '8px 9px', color: C.muted, fontWeight: 600, fontSize: 10.5,
  textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid ' + C.line, whiteSpace: 'nowrap',
};
const td = { padding: '8px 9px', fontSize: 12.5, borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
const badge = {
  display: 'inline-block', minWidth: 34, padding: '2px 7px', border: '1.5px solid',
  borderRadius: 6, fontSize: 11.5, fontWeight: 700, textAlign: 'center',
};

/* Este carimbo nasceu como o UNICO lugar da ficha que explicava a coluna vazia.
   Desde que o veredito passou a aparecer sempre (src/ocVeredito.js), duas das
   tres frases daqui viraram eco do bloco de cima — e a mesma frase dita duas
   vezes na mesma tela nao informa o dobro, ela faz o leitor procurar a
   diferenca que nao existe. Ficou so o que o veredito NAO diz: quantas acoes,
   de quando a quando, de quais agencias. */
function CarimboCobertura({ cob }) {
  if (!cob.temAgencia) return null;   // ja dito, com mais contexto, no veredito
  return (
    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
      Cobertura: <b>{cob.fontes.join(', ')}</b> · {cob.nPontosAg} acao(oes) de {dBR(cob.desde)} a {dBR(cob.ate)}
      {cob.escalas.length ? <> · escala {cob.escalas.join(', ')}</> : null}.
    </div>
  );
}

const tomCor = (tom) => (tom === 'bom' ? C.green : tom === 'atencao' ? C.amber2 : C.blue);

export default function OcTrilhaAgencias({ track, vereditos }) {
  const [tudo, setTudo] = useState(false);
  const { linhas, cobertura } = useMemo(() => montarLinhas(track), [track]);
  /* A leitura e as notas vigentes sao derivadas, nunca copiadas de fora. O card
     que vinha de "oc_painel.payload" so existia para parte das companhias — e
     ficha muda nao e ficha honesta. Hooks antes do return curto, sempre. */
  const leitura = useMemo(
    () => lerVeredito({ linhas, cobertura, vereditos }), [linhas, cobertura, vereditos]);
  const notas = useMemo(() => notasVigentes(track, FONTE_RM), [track]);
  if (!linhas.length) return null;

  const LIMITE = 12;
  const visiveis = tudo ? linhas : linhas.slice(0, LIMITE);

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, borderBottom: '2px solid ' + C.navy, padding: '0 0 6px', margin: '22px 0 10px' }}>
        Historico RiskMind x agencia
      </div>

      {/* SEMPRE aparece — ver src/ocVeredito.js e o portao G26. Silencio nao e
          um estado: sem cobertura, nota so em escala nacional e divergencia
          aberta sao coisas diferentes, e cada uma precisa estar escrita. */}
      <div style={{ ...box, marginBottom: 10, fontSize: 12, borderLeft: '4px solid ' + tomCor(leitura.tom) }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 11.5,
            fontWeight: 700, color: '#fff', background: tomCor(leitura.tom),
          }}>{leitura.rotulo}</span>
          {notas.map((n) => (
            <span key={'n' + n.fonte} style={{ fontSize: 11.5, color: C.muted }}>
              <b style={{ color: C.agency }}>{n.fonte}</b>{' '}
              <span style={{ ...badge, minWidth: 0, color: C.agency, borderColor: C.agency }}>{n.rating}</span>{' '}
              <span style={{ fontSize: 10 }}>escala {n.escala || 'n/d'} · desde {dBR(n.desde)}</span>
            </span>
          ))}
        </div>
        <div style={{ marginTop: 7, lineHeight: 1.55, color: C.ink }}>{leitura.texto}</div>
      </div>

      {/* O LOG, NAO O VEREDITO. O bloco de cima ja mostra o evento mais recente
          por extenso; quando ha UM so, repetir aqui e a mesma frase duas vezes.
          Com dois ou mais, a sequencia passa a ser a informacao — da para ver a
          agencia mudando de opiniao ao longo do tempo — e ai a lista paga o
          espaco que ocupa. */}
      {(vereditos || []).length > 1 ? (
        <div style={{ ...box, marginBottom: 10, fontSize: 12 }}>
          <div style={{ fontSize: 10.5, letterSpacing: 0.6, color: C.muted, textTransform: 'uppercase', paddingBottom: 4 }}>
            Eventos de agencia ja avaliados
          </div>
          {(vereditos || []).map((v, i) => (
            <div key={'v' + i} style={{ padding: '5px 0' }}>
              <span style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 11.5,
                fontWeight: 700, color: '#fff',
                background: /antecip/i.test(v.veredito) ? C.green : /diverg/i.test(v.veredito) ? C.amber2 : C.blue,
              }}>{v.veredito}</span>
              <span style={{ marginLeft: 8 }}>
                <b>{v.agencia}</b> {v.rating_novo} ({v.escala}) em {d10(v.data_pub)} — RiskMind ja marcava{' '}
                {v.rm_rating_asof} / PD {Number(v.rm_pd_asof).toFixed(1)}%
                {v.antecedencia_meses > 0 ? ' ha ' + v.antecedencia_meses + ' meses' : ''}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ ...box, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={th}>trimestre</th>
              <th style={th} title="data em que o RiskMind soube; ~ marca data estimada pelo prazo regulatorio">observado em</th>
              <th style={{ ...th, textAlign: 'center', color: C.blue }}>rating RiskMind<div style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>escala rm</div></th>
              <th style={{ ...th, textAlign: 'right', color: C.blue }}>PD RiskMind</th>
              <th style={{ ...th, textAlign: 'center', color: C.agency }}>rating agencia</th>
              <th style={{ ...th, textAlign: 'right', color: C.agency }}>PD implicita<div style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>1 ano, escala global</div></th>
              <th style={{ ...th, textAlign: 'right' }}>Δ p.p.</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((l) => (
              <tr key={l.data} style={l.agAcao ? { background: '#faf7ff' } : undefined}>
                <td style={{ ...td, fontWeight: 600, color: C.navy }}>
                  {l.dataBase ? qlabel(l.dataBase) : '—'}
                  <span style={{ color: C.muted, fontWeight: 400, marginLeft: 6, fontSize: 11 }}>
                    {l.dataBase ? dBR(l.dataBase) : ''}
                  </span>
                </td>
                <td style={{ ...td, color: C.muted }}>
                  {dBR(l.data)}
                  {l.obsOrigem && l.obsOrigem.indexOf('prazo_regulatorio') === 0 ? (
                    <span title="data estimada pelo prazo regulatorio de entrega, nao medida"
                      style={{ marginLeft: 5, fontSize: 10, color: C.amber2 }}>~</span>
                  ) : null}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  {l.rmRating
                    ? <span style={{ ...badge, color: corRating(l.rmRating), borderColor: corRating(l.rmRating) }}>{l.rmRating}</span>
                    : <span style={{ color: C.muted }}>—</span>}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pd1(l.rmPd)}</td>
                <td style={{ ...td, textAlign: 'center' }}>
                  {l.agRating ? (
                    <div>
                      <span style={{ ...badge, color: C.agency, borderColor: C.agency, background: l.agAcao ? '#f3eefc' : 'transparent' }}>
                        {l.agRating}{l.agAcao ? ' *' : ''}
                      </span>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                        {l.agFonte}{l.agEscala ? ' · ' + l.agEscala : ''}
                      </div>
                    </div>
                  ) : (
                    <span style={{ color: C.muted, fontSize: 11 }}>
                      {l.agAusencia === 'sem_cobertura' ? '—' : 'antes da cobertura'}
                    </span>
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {l.agPd != null
                    ? pd1(l.agPd)
                    : <span title={l.agPdMotivo || ''} style={{ color: C.muted }}>—</span>}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                  color: l.delta == null ? C.muted : (l.delta > 0 ? C.red : C.green), fontWeight: l.delta == null ? 400 : 600 }}>
                  {l.delta == null ? '—' : (l.delta > 0 ? '+' : '') + l.delta.toFixed(2).replace('.', ',')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {linhas.length > LIMITE ? (
        <button type="button" onClick={() => setTudo(!tudo)}
          style={{ marginTop: 8, padding: '6px 12px', border: '1px solid ' + C.line, borderRadius: 8, background: C.paper, color: C.navy, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          {tudo ? 'mostrar so os ' + LIMITE + ' mais recentes' : 'mostrar as ' + linhas.length + ' observacoes'}
        </button>
      ) : null}

      <CarimboCobertura cob={cobertura} />

      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
        <b>*</b> a agencia se moveu nesta linha; nas demais vale a nota anterior.
        As letras nao se convertem entre escalas — a comparacao que vale e a de <b>PD</b>.
        PD implicita: {FONTE_PD}. Sao objetos diferentes: media de coorte de longo prazo
        contra saida point-in-time do motor. Δ e a diferenca em pontos percentuais.
      </div>
    </div>
  );
}
